import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, Upload, FileText, Lock, CheckCircle2 } from "lucide-react";

function UploadTile({ label, docType, fileId, onUploaded }) {
  const ref = useRef();
  const [uploading, setUploading] = useState(false);
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("doc_type", docType);
      const { data } = await api.post("/kyc/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded(data.file_id);
      toast.success(`${label} uploaded`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };
  return (
    <button type="button" data-testid={`upload-${docType}`} onClick={() => ref.current?.click()}
      className={`w-full border-2 border-dashed rounded-2xl p-6 flex flex-col items-center justify-center gap-3 text-center transition-colors ${fileId ? "border-emerald-500 bg-emerald-50" : "border-slate-300 hover:border-orange-600 bg-slate-50"}`}>
      <input ref={ref} type="file" hidden accept="image/*,.pdf" onChange={onFile} />
      {fileId ? (
        <>
          <CheckCircle2 size={32} className="text-emerald-600" />
          <div className="font-semibold text-emerald-700">Uploaded · click to replace</div>
        </>
      ) : (
        <>
          <div className="w-12 h-12 rounded-xl bg-white border border-slate-200 grid place-items-center"><Upload size={20} className="text-orange-800" /></div>
          <div>
            <div className="font-semibold">{uploading ? "Uploading…" : label}</div>
            <div className="text-xs text-slate-500 mt-1">JPG, PNG or PDF · Max 8MB</div>
          </div>
        </>
      )}
    </button>
  );
}

export default function KYC() {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [studentFile, setStudentFile] = useState("");
  const [studentBackFile, setStudentBackFile] = useState("");
  const [form, setForm] = useState({ student_aadhaar_number: "", guardian_name: "", guardian_phone: "" });
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    api.get("/kyc/status").then((r) => {
      if (r.data.kyc_status === "verified" || r.data.kyc_status === "pending") setSubmitted(true);
      if (r.data.kyc) setForm({
        student_aadhaar_number: r.data.kyc.student_aadhaar_number || "",
        guardian_name: r.data.kyc.guardian_name || "",
        guardian_phone: r.data.kyc.guardian_phone || "",
      });
    });
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!studentFile) { toast.error("Please upload the FRONT of your Aadhaar"); return; }
    if (!studentBackFile) { toast.error("Please upload the BACK of your Aadhaar"); return; }
    if (form.student_aadhaar_number.replace(/\s/g, "").length !== 12) { toast.error("Aadhaar must be 12 digits"); return; }
    if (!form.guardian_name.trim() || !form.guardian_phone.trim()) { toast.error("Please provide guardian name and phone"); return; }
    try {
      await api.post("/kyc/submit", {
        ...form,
        student_aadhaar_file_id: studentFile,
        student_aadhaar_back_file_id: studentBackFile,
      });
      toast.success("KYC submitted for review");
      setSubmitted(true);
      const me = (await api.get("/auth/me")).data;
      setUser(me); localStorage.setItem("hn_user", JSON.stringify(me));
      setTimeout(() => nav("/dashboard"), 800);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submit failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#FBF5EA]">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="text-xs tracking-[0.25em] uppercase font-bold text-amber-600">Verify your identity</div>
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-1">Aadhaar KYC</h1>
        <p className="mt-2 text-slate-600 max-w-2xl">HostelNow is for students under 30. We require your Aadhaar for identity verification, plus a parent/guardian's name and phone for emergency contact. Documents are encrypted and only visible to hostel admin.</p>

        {!submitted && user?.kyc_status === "rejected" && user?.kyc_rejection_reason && (
          <div data-testid="kyc-rejection-notice" className="mt-6 bg-rose-50 border-2 border-rose-300 rounded-2xl p-5 flex items-start gap-3">
            <div className="text-rose-600 text-2xl">⚠️</div>
            <div>
              <div className="font-bold text-rose-900">Previous submission was rejected</div>
              <p className="text-sm text-rose-900 mt-1">Reason: <span className="font-semibold">{user.kyc_rejection_reason}</span></p>
              <p className="text-xs text-rose-700 mt-2">Please re-upload clearer photos below and try again.</p>
            </div>
          </div>
        )}

        {!submitted && (
          <div className="mt-8 bg-gradient-to-br from-[#0B4E9B] to-[#083A75] text-white rounded-2xl p-6 sm:p-8">
            <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-amber-300">Reviewed by HostelNow team</div>
            <h3 className="mt-2 font-display font-black text-2xl">Submit your documents for verification.</h3>
            <p className="mt-2 text-sm text-white/80 max-w-xl">Our admin team manually reviews every submission within 24 hours. This ensures every hostel gets a real, verified student.</p>
          </div>
        )}

        {submitted ? (
          <div className="mt-10 bg-emerald-50 border border-emerald-200 rounded-2xl p-8 flex items-start gap-4">
            <CheckCircle2 className="text-emerald-600" size={28} />
            <div>
              <div className="font-display font-bold text-2xl">KYC submitted for review</div>
              <p className="text-slate-700 mt-1">Our admin team is reviewing your documents. You&apos;ll be notified once verified (usually within 24 hours). You can browse and book hostels — payment confirms your reservation once KYC is approved.</p>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-10 grid gap-8">
            <div className="bg-white border border-slate-200 rounded-2xl p-8">
              <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-orange-800"><ShieldCheck size={14} /> Student Aadhaar</div>
              <h2 className="mt-2 font-display font-bold text-2xl">Your Aadhaar</h2>
              <p className="mt-1 text-sm text-slate-500">Upload <b>both sides</b> of your Aadhaar card as separate photos. Both are required.</p>
              <div className="mt-6 grid md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <Label className="text-xs tracking-widest uppercase font-bold">Aadhaar number</Label>
                  <Input data-testid="kyc-student-aadhaar" required inputMode="numeric" maxLength={14} value={form.student_aadhaar_number} onChange={(e) => setForm({ ...form, student_aadhaar_number: e.target.value })} placeholder="1234 5678 9012" className="mt-1 h-12" />
                </div>
                <UploadTile
                  label="Aadhaar FRONT (photo side)"
                  docType="student_aadhaar"
                  fileId={studentFile}
                  onUploaded={setStudentFile}
                />
                <UploadTile
                  label="Aadhaar BACK (address side)"
                  docType="student_aadhaar_back"
                  fileId={studentBackFile}
                  onUploaded={setStudentBackFile}
                />
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl p-8">
              <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-amber-600"><ShieldCheck size={14} /> Guardian details</div>
              <h2 className="mt-2 font-display font-bold text-2xl">Parent / Guardian contact</h2>
              <p className="mt-1 text-sm text-slate-500">For emergency purposes only. We won't contact them unless needed.</p>
              <div className="mt-6 grid md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-xs tracking-widest uppercase font-bold">Full name</Label>
                  <Input data-testid="kyc-guardian-name" required value={form.guardian_name} onChange={(e) => setForm({ ...form, guardian_name: e.target.value })} className="mt-1 h-12" />
                </div>
                <div>
                  <Label className="text-xs tracking-widest uppercase font-bold">Phone</Label>
                  <Input data-testid="kyc-guardian-phone" required value={form.guardian_phone} onChange={(e) => setForm({ ...form, guardian_phone: e.target.value })} placeholder="98765 43210" className="mt-1 h-12" />
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between bg-white border border-slate-200 rounded-2xl p-5">
              <div className="flex items-center gap-3 text-sm text-slate-600">
                <Lock size={16} className="text-orange-800" /> Documents encrypted at rest. Not sold to third parties.
              </div>
              <Button data-testid="kyc-submit" type="submit" className="rounded-full bg-orange-800 hover:bg-orange-800 text-white h-12 px-8 active:scale-95 transition-transform">
                Submit for verification
              </Button>
            </div>
          </form>
        )}
      </div>
      <Footer />
    </div>
  );
}
